import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { api, ApiError } from '../lib/api'
import type { CreateDraftResponse } from '../types/protocol'

export default function Landing() {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  const [joinCode, setJoinCode] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function createDraft(event: FormEvent) {
    event.preventDefault()
    setCreating(true)
    setError(null)
    try {
      const response = await api<CreateDraftResponse>('/drafts', {
        method: 'POST',
        body: { name: name.trim() || 'Draft Night' },
      })
      navigate(`/setup#${response.commissioner_token}`)
    } catch (e) {
      if (e instanceof ApiError && e.code === 'rate_limited') {
        setError('Too many attempts — wait a minute and try again.')
      } else {
        setError(e instanceof ApiError ? e.message : 'Could not create the draft')
      }
      setCreating(false)
    }
  }

  function joinDraft(event: FormEvent) {
    event.preventDefault()
    const code = joinCode.trim().toUpperCase()
    if (code) navigate(`/join/${code}`)
  }

  return (
    <main className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-4 py-16">
      {/* A single soft pool of light behind the wordmark. One light source on
          the whole page — it is what makes the two cards below read as sitting
          in a room rather than floating on a flat field. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-[-18vh] left-1/2 h-[60vh] w-[110vw] max-w-[1100px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgb(240_180_41/0.10),transparent_65%)]"
      />

      <header className="relative flex flex-col items-center text-center">
        <p className="eyebrow animate-rise">Draft night, one screen</p>
        <h1 className="animate-rise mt-3 font-display text-6xl leading-[0.9] font-bold text-ink-hi sm:text-7xl [animation-delay:60ms]">
          Fantasy Drafter
        </h1>
        {/* The rule is the identity mark: a gold hairline that appears on every
            page as the eyebrow colour and here as an object in its own right. */}
        <div className="animate-rise mt-5 h-px w-24 bg-gradient-to-r from-transparent via-signal to-transparent [animation-delay:120ms]" />
        <p className="animate-rise mt-5 max-w-md text-ink-mid [animation-delay:160ms]">
          Run your league&apos;s live draft from the couch — a board on the TV, a room in
          everyone&apos;s pocket.
        </p>
      </header>

      <div className="relative mt-12 grid w-full max-w-2xl gap-4 sm:grid-cols-2">
        <form
          onSubmit={(event) => void createDraft(event)}
          className="animate-rise flex flex-col rounded-lg border border-line bg-surface-2 p-6 shadow-raised [animation-delay:220ms]"
        >
          <h2 className="eyebrow">Start a draft</h2>
          <p className="mt-2 font-display text-2xl leading-tight font-bold text-ink-hi">
            You run it
          </p>
          <p className="mt-1 text-sm text-ink-lo">You&apos;ll be the commissioner.</p>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Draft name"
            aria-label="Draft name"
            className="mt-5 w-full rounded-md border border-line bg-surface-3 px-3 py-2.5 text-sm text-ink-hi shadow-[inset_0_1px_2px_rgb(0_0_0/0.4)] placeholder:text-ink-lo focus:border-signal/60 focus:outline-none"
          />
          <button
            type="submit"
            disabled={creating}
            className="mt-3 w-full rounded-md bg-signal px-4 py-2.5 text-sm font-bold text-ink shadow-raised hover:bg-signal-dim disabled:opacity-50"
          >
            {creating ? 'Creating…' : 'Create draft'}
          </button>
        </form>

        <form
          onSubmit={joinDraft}
          className="animate-rise flex flex-col rounded-lg border border-line bg-surface-2 p-6 shadow-raised [animation-delay:280ms]"
        >
          <h2 className="eyebrow">Join a draft</h2>
          <p className="mt-2 font-display text-2xl leading-tight font-bold text-ink-hi">
            You&apos;re invited
          </p>
          <p className="mt-1 text-sm text-ink-lo">Enter the code from the board.</p>
          <input
            value={joinCode}
            onChange={(event) => setJoinCode(event.target.value.toUpperCase())}
            placeholder="CODE"
            aria-label="Join code"
            maxLength={6}
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            className="mt-5 w-full rounded-md border border-line bg-surface-3 px-3 py-2.5 text-center font-mono text-lg tracking-[0.5em] text-signal uppercase shadow-[inset_0_1px_2px_rgb(0_0_0/0.4)] placeholder:text-ink-lo focus:border-signal/60 focus:outline-none"
          />
          {/* Codes are six characters now, but 4 stays the *minimum* rather
              than the only length: drafts created before Phase 13 still have
              four-character codes printed on boards and baked into QRs, and
              they have to submit from this field. */}
          <button
            type="submit"
            disabled={joinCode.trim().length < 4}
            className="mt-3 w-full rounded-md border border-line-strong bg-surface-3 px-4 py-2.5 text-sm font-bold text-ink-mid shadow-raised hover:text-ink-hi disabled:opacity-40"
          >
            Join
          </button>
        </form>
      </div>

      {error && <p className="relative mt-6 text-sm text-crit">{error}</p>}

      <Link
        to="/admin"
        title="Operator dashboard — player database and draft housekeeping"
        className="relative mt-12 font-mono text-[11px] tracking-[0.18em] text-ink-lo uppercase underline-offset-4 hover:text-ink-lo hover:underline"
      >
        Admin
      </Link>
    </main>
  )
}
